// SGNL-104 — side panel showing top-3 rationales for a tag, anchored
// to the right edge of the viewport. Used by the Partner Opps tab's
// ⓘ button.
//
// `/detail` cache is shared with `tag_popover.ts` via
// `opportunity_detail_cache.ts` (PR #57 review F1) so a click-popover-
// then-open-panel sequence on the same opp only fetches once.
//
// Singleton element. Esc closes.

import type { OpportunityRow } from "../types";
import { esc } from "./format";
import { getOpportunityDetail } from "./opportunity_detail_cache";

interface TopOpp {
  opp_id: string;
  opp_name: string;
  account_name: string | null;
  confidence: number;
}

let panelEl: HTMLElement | null = null;
let escHandler: ((ev: KeyboardEvent) => void) | null = null;
// PR-Agent T2 on PR #57 — open-generation counter. If the user
// opens the panel for tag A, then quickly opens for tag B before
// A's `Promise.all` resolves, A's late-resolving render would
// otherwise overwrite B's. Each `openTagRationalesPanel` bumps the
// generation; the awaiter snapshots the value at call time and
// no-ops the render if the generation has advanced.
let openGeneration = 0;

function ensurePanelEl(): HTMLElement {
  if (panelEl) return panelEl;
  const el = document.createElement("aside");
  el.setAttribute("data-testid", "tag-rationales-panel");
  el.setAttribute("role", "dialog");
  el.style.cssText = [
    "position:fixed",
    "top:0",
    "right:0",
    "width:380px",
    "max-width:90vw",
    "height:100vh",
    "z-index:1000",
    "padding:16px 20px",
    "background:var(--bg-card, #fff)",
    "color:var(--ink, #0f172a)",
    "border-left:1px solid var(--line, #e5e7eb)",
    "box-shadow:-4px 0 12px rgba(0,0,0,0.15)",
    "font-size:13px",
    "line-height:1.5",
    "overflow-y:auto",
    "display:none",
  ].join(";");
  // Close-button delegated listener — once-per-singleton.
  el.addEventListener("click", (ev) => {
    const tgt = ev.target as HTMLElement;
    if (tgt.dataset.action === "close-panel") {
      ev.stopPropagation();
      closeTagRationalesPanel();
    }
  });
  document.body.appendChild(el);
  panelEl = el;
  return el;
}

export function closeTagRationalesPanel(): void {
  if (panelEl) panelEl.style.display = "none";
  if (escHandler) {
    document.removeEventListener("keydown", escHandler);
    escHandler = null;
  }
}

function pickTop3(tagKey: string, rows: OpportunityRow[]): TopOpp[] {
  // For each opp, find its yes-verdict entry for this tag (if any),
  // collect with confidence. Sort desc, take top 3.
  const candidates: TopOpp[] = [];
  for (const r of rows) {
    const t = (r.tags ?? []).find((x) => x.tag_key === tagKey && x.verdict === "yes");
    if (!t) continue;
    candidates.push({
      opp_id: r.id,
      opp_name: r.name ?? r.id,
      account_name: r.account_name ?? null,
      confidence: t.confidence,
    });
  }
  candidates.sort((a, b) => b.confidence - a.confidence);
  return candidates.slice(0, 3);
}

function renderHeader(tagKey: string, totalOpps: number): string {
  return (
    `<div style="display:flex; justify-content:space-between; align-items:flex-start; gap:12px; margin-bottom:12px;">` +
    `<div>` +
    `<h3 style="margin:0; font-size:16px;">Why "${esc(tagKey)}"?</h3>` +
    `<div class="muted" style="font-size:11px; margin-top:2px;">` +
    `Top ${Math.min(3, totalOpps)} of ${totalOpps} tagged opps by confidence` +
    `</div>` +
    `</div>` +
    `<button type="button" class="btn-icon-close" data-action="close-panel" ` +
    `aria-label="Close panel">` +
    `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">` +
    `<path d="M1 1l14 14M15 1L1 15" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>` +
    `</svg></button>` +
    `</div>`
  );
}

function renderRationaleRow(top: TopOpp, rationale: string | null): string {
  const conf = (top.confidence * 100).toFixed(0);
  const acct = top.account_name ? ` · ${esc(top.account_name)}` : "";
  return (
    `<div style="margin-bottom:14px; padding-bottom:12px; ` +
    `border-bottom:1px solid var(--line, #e5e7eb);" ` +
    `data-testid="tag-rationale-item">` +
    `<div style="display:flex; justify-content:space-between; gap:8px;">` +
    `<a href="#opp/${esc(top.opp_id)}" style="font-weight:600; flex:1;">` +
    `${esc(top.opp_name)}</a>` +
    `<span class="muted" style="font-size:11px;">${esc(conf)}%</span>` +
    `</div>` +
    `<div class="muted" style="font-size:11px; margin-top:1px;">` +
    `${esc(top.opp_id)}${acct}` +
    `</div>` +
    `<div style="margin-top:6px; white-space:pre-wrap;">${esc(rationale ?? "(no rationale recorded)")}</div>` +
    `</div>`
  );
}

export async function openTagRationalesPanel(
  tagKey: string,
  rows: OpportunityRow[],
): Promise<void> {
  // PR #57 review F2: rapid re-open with a still-pending first call
  // would leak the prior `escHandler` (it points at the closure from
  // the previous open). `closeTagRationalesPanel()` deregisters the
  // current handler if any, so calling it at the top guards against
  // that leak.
  closeTagRationalesPanel();

  // Snapshot generation BEFORE awaiting; bump for this call.
  const myGeneration = ++openGeneration;

  const el = ensurePanelEl();
  const totalOpps = rows.filter((r) =>
    (r.tags ?? []).some((t) => t.tag_key === tagKey && t.verdict === "yes"),
  ).length;
  const top = pickTop3(tagKey, rows);

  el.innerHTML =
    renderHeader(tagKey, totalOpps) +
    `<div data-testid="tag-rationale-loading" class="muted">Loading rationales…</div>`;
  el.style.display = "block";

  // Esc handler — install on open, remove on close.
  escHandler = (ev: KeyboardEvent) => {
    if (ev.key === "Escape") closeTagRationalesPanel();
  };
  document.addEventListener("keydown", escHandler);

  // Lazy-fetch detail for each of the top 3 in parallel via the
  // shared cache (PR #57 review F1).
  const fetches = top.map((t) =>
    getOpportunityDetail(t.opp_id).then(
      (detail) => {
        const tag = (detail.tags ?? []).find((x) => x.tag_key === tagKey) ?? null;
        return { top: t, rationale: tag?.rationale ?? null };
      },
      (err: unknown) => {
        // Cache eviction handled by getOpportunityDetail's own catch.
        // Emit a placeholder row instead of failing the whole panel.
        const msg = err instanceof Error ? err.message : String(err);
        return { top: t, rationale: `(failed to load: ${msg})` };
      },
    ),
  );

  const results = await Promise.all(fetches);
  // PR-Agent T2: drop late-resolving renders on the floor if a newer
  // `openTagRationalesPanel` call has already taken over. Without
  // this, A's late results would overwrite B's panel content.
  if (myGeneration !== openGeneration) return;
  el.innerHTML =
    renderHeader(tagKey, totalOpps) +
    (results.length === 0
      ? `<div class="muted">No tagged opps for this tag.</div>`
      : results.map((r) => renderRationaleRow(r.top, r.rationale)).join(""));
}

/** Test-only: wipe panel element. Cache lives in
 * `opportunity_detail_cache.ts`; clear that separately if needed. */
export function _clearTagRationalesPanelCache(): void {
  closeTagRationalesPanel();
  if (panelEl) {
    panelEl.remove();
    panelEl = null;
  }
}
